import { Component, ViewChild } from '@angular/core';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { CalendarOptions, EventInput } from '@fullcalendar/core';
import deLocale from '@fullcalendar/core/locales/de';
import dayGridPlugin from '@fullcalendar/daygrid';
import { ResourceInput } from '@fullcalendar/resource';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { MenuItem } from 'primeng/api';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { EnaioDocumentsDefinitionComponent } from 'src/modules/enaio-documents/components/enaio-documents-definition/enaio-documents-definition.component';
import { EnaioDocumentFrontendResult } from 'src/modules/enaio-documents/models/enaio-document-frontend-result.model';
import { EnaioDocument } from 'src/modules/enaio/shared/EnaioDocument';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { PortalWorkflowUser } from '../../models/portal-workflow-user.model';

@Component({
  selector: 'app-personell-file',
  templateUrl: './personell-file.component.html',
  styleUrl: './personell-file.component.scss'
})
export class PersonellFileComponent extends ComponentView {

    user: DatabaseUser;
    personell: PortalWorkflowUser[];
    definitions: EnaioDocumentFrontendResult[] = [];
    personellGui: MenuItem[];
    documentTypesGui: MenuItem[];
    selectedPersonell = 0;
    selectedDocumentType = "vacationRequests";
    activeTabIndex = 0;
    calendarTitle = "";

    @ViewChild("defComponent")
    defComponent: EnaioDocumentsDefinitionComponent;
    @ViewChild("fc") 
    fc: FullCalendarComponent;

    eventTypes = [
        new FrontendFieldListItem("vacationRequest", this.app.t("customers.formeld.personellFile.calendar.eventTypeVacationRequest")),
        new FrontendFieldListItem("sickNote", this.app.t("customers.formeld.personellFile.calendar.eventTypeSickNote")),
        new FrontendFieldListItem("flexDaysRequest", this.app.t("customers.formeld.personellFile.calendar.eventTypeFlexDaysRequest")),
        new FrontendFieldListItem("compensationDay1", this.app.t("customers.formeld.personellFile.calendar.eventTypeCompensationDay1")),
        new FrontendFieldListItem("compensationDay2", this.app.t("customers.formeld.personellFile.calendar.eventTypeCompensationDay2")),
    ];

    calendarSelectedEventTypes = this.eventTypes.map(t => t.value);

    calendarViews = [
        {
            label: this.app.t("customers.formeld.personellFile.calendar.viewGantt"),
            value: "resourceTimelineMonth"
        },
        {
            label: this.app.t("customers.formeld.personellFile.calendar.viewTimeline"),
            value: "timelineMonth"
        },
        {
            label: this.app.t("customers.formeld.personellFile.calendar.viewGrid"),
            value: "dayGridMonth"
        }        
    ];
    selectedCalendarView = "resourceTimelineMonth"

    resources: ResourceInput[];
    resourcesById: OrdinaryObject<string> = {};

    employeesGui: FrontendFieldListItem[];
    calendarSelectedEmployees: string[];
   
    calendarOptions: CalendarOptions = {
        timeZone: 'UTC',
        initialView: 'resourceTimelineMonth',
        schedulerLicenseKey: 'CC-Attribution-NonCommercial-NoDerivatives',
        plugins: [resourceTimelinePlugin, dayGridPlugin],
        firstDay: 1,
        locale: deLocale,
        resourceAreaWidth: 300,
        aspectRatio: 100, //Kleiner Hack, um zu verhindern, dass die Höhe zu groß wird und das Fenster scrollt
        displayEventTime: false,
        eventOverlap: false,
        headerToolbar: {
          left: "",
          center: "",
          right: ""
        },
        editable: true,
        resourceOrder: "title",
        resources: [
        ], 
        events: [
        ],
        eventMouseEnter: (info) => {
            const tooltipElement = document.createElement('div');
            tooltipElement.innerHTML = info.event.title + "<br>" + this.resourcesById[info.event.extendedProps.resourceId] + "<br>" + 
                Utils.dateFormatDefaultDate(info.event.start) + " - " + Utils.dateFormatDefaultDate(info.event.end);
            tooltipElement.className = 'custom-tooltip';
            document.body.appendChild(tooltipElement);

            const rect = info.el.getBoundingClientRect();
            tooltipElement.style.position = 'absolute';
            tooltipElement.style.top = `${rect.top + window.scrollY - tooltipElement.offsetHeight - 5}px`;
            tooltipElement.style.left = `${rect.left + window.scrollX}px`;
            tooltipElement.style.zIndex = '1000';

            (info.el as any)._tooltipElement = tooltipElement;
        },
        eventMouseLeave: (info) => {
            if ((info.el as any)._tooltipElement) {
                document.body.removeChild((info.el as any)._tooltipElement);
                (info.el as any)._tooltipElement = null;
            }
        }
      };

    ngDoCheck(_): void {
        this.calendarTitle = this.getCalendarTitle();
    }

    async initParams(): Promise<boolean> {
        this.user = this.app.auth.userDetails.getDatabaseUser();
        this.personell = Utils.arraySortBy((await RestEndpoint.main().run("api/wfl/tbr/getPersonell2").list(PortalWorkflowUser)), doc => doc.lastName + ", " + doc.firstName);
        this.definitions = Utils.arraySortBy(await RestEndpoint.main().query({ includeResultCounts: false }).run("api/doc/def").list(EnaioDocumentFrontendResult), def => def.name);
        for (let def of this.definitions) {
            def.translate(this.app);
        }

        this.resources = Utils.arraySortBy(this.personell.map(p => ({ id: Utils.toString(p.folderId), title: p.lastName + ", " + p.firstName })), p => p.title);
        this.resourcesById = Utils.arrayConvertToOrdinaryObject(this.resources, r => r.id, r => r.title);

        this.employeesGui = this.resources.map(r => new FrontendFieldListItem(r.id, r.title)),
        this.calendarSelectedEmployees = this.resources.map(r => r.id),

        this.personell = this.personell.filter(user => user.folderId != this.user.getAttributeValueNumber("folderId"));

        this.personellGui = Utils.arrayWithoutNull([{ label: this.user.getAttributeValue("lastName") + ", " + this.user.getAttributeValue("firstName"), value: 0 },
            this.personell.length == 0 ? null : { label: "<Alle>", value: -1 },
            ...this.personell.map(p => ({ label: p.lastName + ", " + p.firstName, value: p.folderId }))]);
        GuiUtils.angularTimer(async () => this.changePersonell());

        this.defComponent.init();

        this.app.updateNavigation(this.app.t("customers.formeld.personellFile.title"), [
            { label: this.app.t("customers.formeld.personellFile.title"), routerLink: ["/enaio-workflows", "personell-file"]}
        ]);

        return true;
    }

    async changePersonell(): Promise<void> {
        let zeitwirtschaft = this.selectedPersonell == -1 || this.selectedPersonell == 0 && this.user.getAttributeValueBool("zeitwirtschaft") || this.personell.find(p => p.folderId == this.selectedPersonell)?.zeitwirtschaft;
        let displayPaySlips = this.user.getAttributeValueBool("displayPaySlips");
        this.documentTypesGui = this.definitions.filter(def => def.defId == "flexDaysRequests" || def.defId == "compensationDays" ? zeitwirtschaft : def.defId == "payslips" ? displayPaySlips : true)
            .map(def => ({ label: def.name, value: def.defId }));
        await this.change();
    }

    async change(): Promise<void> {
        this.defComponent.defId = this.selectedDocumentType;
        this.defComponent.queryOptions = this.selectedPersonell == 0 ? null : {
            all: this.selectedPersonell == -1,
            folderId: this.selectedPersonell == -1 ? 0 : this.selectedPersonell
        };
        await this.defComponent.reload(def => {
            if (this.selectedPersonell != -1) {
                def.columns = def.columns.filter(col => col.title != "Nachname" && col.title != "Vorname");
            }
        });
    }

    async changeActiveTabIndex(): Promise<void> {
        if (this.activeTabIndex == 1) {
            await this.initCalendar();
        }
    }

    async changeCalendarView(): Promise<void> {
        this.fc.getApi().changeView(this.selectedCalendarView);
    }

    updateCalendarFilter(): void {
        this.fc.getApi().refetchResources();
        this.fc.getApi().refetchEvents();
    }

    async initCalendar(): Promise<void> {
        if (this.fc == null) {
            console.log("initCalendar abgebrochen. this.fc war null");
            Utils.setTimerOnce(1000, () => this.initCalendar());
            return;
        }
        let colors = Utils.getMaterialColors();

        this.calendarOptions.resources = () => {
            let employees = new Set<string>(this.calendarSelectedEmployees);
            return Promise.resolve(this.resources.filter(r => employees.has(r.id)));
        }

        let events: EventInput[] = [];

        for (let e of (await RestEndpoint.main().query({ defId: "compensationDays", queryOptions: Utils.toJson({ all: true })}).run("api/doc/def/search").get(EnaioDocumentFrontendResult)).documents) {
            events.push(this.createEvent(e.doc, "Arbeitstag", "Arbeitstag", this.app.t("customers.formeld.personellFile.calendar.eventTypeCompensationDay1"), "compensationDay1", colors[14]));
            events.push(this.createEvent(e.doc, "Ausgleichstag", "Ausgleichstag", this.app.t("customers.formeld.personellFile.calendar.eventTypeCompensationDay2"), "compensationDay2", colors[15]));            
        }
        let flexDaysRequests = (await RestEndpoint.main().query({ defId: "flexDaysRequests", queryOptions: Utils.toJson({ all: true })}).run("api/doc/def/search").get(EnaioDocumentFrontendResult)).documents;
        for (let e of flexDaysRequests) {
            if (!e.doc.getBoolByInternal("Stornoantrag") && e.doc.getStringByInternal("Status") == "freigegeben") {
                events.push(this.createEvent(e.doc, "Gleitzeitvon", "Gleitzeitbis", this.app.t("customers.formeld.personellFile.calendar.eventTypeFlexDaysRequest"), "flexDaysRequest", colors[9]));
            }
        }
        for (let e of flexDaysRequests) {
            if (e.doc.getBoolByInternal("Stornoantrag") && e.doc.getStringByInternal("Status") == "freigegeben") {
                events = this.processCancellationRequest(e.doc, "Gleitzeitvon", "Gleitzeitbis", "flexDaysRequest", events);
            }
        }
        let vacationRequests = (await RestEndpoint.main().query({ defId: "vacationRequests", queryOptions: Utils.toJson({ all: true })}).run("api/doc/def/search").get(EnaioDocumentFrontendResult)).documents;
        for (let e of vacationRequests) {
            if (!e.doc.getBoolByInternal("Stornoantrag") && e.doc.getStringByInternal("Status") == "freigegeben") {
                events.push(this.createEvent(e.doc, "Urlaubvon", "Urlaubbis", this.app.t("customers.formeld.personellFile.calendar.eventTypeVacationRequest"), "vacationRequest", colors[5]));
            }
        }
        for (let e of vacationRequests) {
            if (e.doc.getBoolByInternal("Stornoantrag") && e.doc.getStringByInternal("Status") == "freigegeben") {
                events = this.processCancellationRequest(e.doc, "Urlaubvon", "Urlaubbis", "vacationRequest", events);
            }
        }
        for (let e of (await RestEndpoint.main().query({ defId: "sickNotes", queryOptions: Utils.toJson({ all: true })}).run("api/doc/def/search").get(EnaioDocumentFrontendResult)).documents) {
            events.push(this.createEvent(e.doc, "Krankmeldungvon", "Krankmeldungbis", this.app.t("customers.formeld.personellFile.calendar.eventTypeSickNote"), "sickNote", colors[0]));
        }
        this.fc.events = () => {
            let employees = new Set<number>(this.calendarSelectedEmployees.map(s => Utils.toNumber(s)));
            return Promise.resolve(events.filter(e => this.calendarSelectedEventTypes.includes(e.extendedProps.type) && employees.has(e.extendedProps.resourceId)));
        }

        this.calendarOptions.eventDidMount = (info) => {
            if (info.view.type === "dayGridMonth" || info.view.type == "timelineMonth") {
                info.el.querySelector(".fc-event-title").textContent = info.event.title + " " + this.resourcesById[info.event.extendedProps.resourceId];
            }
        }    

        this.updateCalendarFilter();

        this.fc.getApi().render();
    }    

    private createEvent(doc: EnaioDocument, startFieldName: string, endFieldName: string, title: string, type: string, color: number): EventInput {
        return {
            id: Utils.toString(doc.id),
            resourceId: Utils.toString(doc.parent.id),
            start: Utils.dateToUtc(doc.getDateByInternal(startFieldName)),
            end: Utils.dateEndOf(Utils.dateToUtc(doc.getDateByInternal(endFieldName)), "day"),
            title,
            display: "block",
            extendedProps: {
                type,
                resourceId: doc.parent.id,
            },
            backgroundColor: Utils.toColorHexString(color)
        };
    }

    private processCancellationRequest(doc: EnaioDocument, startFieldName: string, endFieldName: string, type: string, allEvents: EventInput[]): EventInput[] {
        let resourceId = Utils.toString(doc.parent.id);
        return Utils.arrayExplode(allEvents, event => {
            if (event.resourceId != resourceId) {
                return [event];
            }
            let start = Utils.dateToUtc(doc.getDateByInternal(startFieldName));
            let end = Utils.dateEndOf(Utils.dateToUtc(doc.getDateByInternal(endFieldName)), "day");
            if (Utils.dateEq(event.start as Date, start) && Utils.dateEq(event.end as Date, end)) {
                return [];
            }
            else {
                return [event]
            }
            //TODO Stornoanträge berücksichtigenn, die nur teilweise überlappen!?
        });
    }

    getCalendarTitle(): string {
        return this.fc?.getApi().getCurrentData().viewTitle ?? "";
    }

    calendarScroll(dir: number): void {
        if (dir < 0) {
            this.fc.getApi().prev();
        }
        else if (dir > 0) {
            this.fc.getApi().next();
        }
    }
}
